import { useMemo } from "react";

import { RoleEnum } from "../models/enums";
import { useAuthenticatedUser } from "./useAuthenticatedUser";

/**
 * Role groups mapping.
 * You can reference these groups by key when checking access.
 */
export const RoleAccess = {
  admin: [
    RoleEnum.ROOT,
    RoleEnum.CC_OPS_ADMIN,
    RoleEnum.CC_OPS,
    RoleEnum.CC_SALES,
    RoleEnum.PARTNER_ADMIN,
    RoleEnum.CUSTOMER_ADMIN,
  ],
  nonAdmin: [
    RoleEnum.CUSTOMER_OPERATOR_FOREMAN,
    RoleEnum.PARTNER_USER,
    RoleEnum.CUSTOMER_OPS_TEAM,
    RoleEnum.CUSTOMER_HSE,
    RoleEnum.CUSTOMER_SECURITY,
    RoleEnum.CC_PROCTORS,
    RoleEnum.LEAKFINDER_DIRECTOR,
    RoleEnum.VERIFIER,
  ],
  operator: [RoleEnum.CUSTOMER_OPERATOR_FOREMAN],
  readonly: [RoleEnum.OPERATOR_READ_ONLY],
};

type RoleGroupKey = keyof typeof RoleAccess;

export type AccessTarget = RoleEnum | RoleEnum[] | RoleGroupKey;

export type AccessOptions = {
  /**
   * If `true`, the result will be inverted (has NOT access).
   * Default: false
   */
  not?: boolean;
};

/**
 * useHasRoleAccess hook
 *
 * Checks whether the authenticated user has access based on:
 *  - a single role (RoleEnum.ROOT)
 *  - multiple roles ([RoleEnum.ROOT, RoleEnum.CC_OPS_ADMIN])
 *  - a predefined role group ('admin', 'nonAdmin', etc.)
 *
 * const isAdmin = useHasRoleAccess('admin');
 * const isNotAdmin = useHasRoleAccess('admin', { not: true });
 *
 * const canCreateAlert = useHasRoleAccess([RoleEnum.ROOT, RoleEnum.CUSTOMER_HSE]);
 * const cannotCreateAlert = useHasRoleAccess([RoleEnum.ROOT], { not: true });
 *
 * const isForemanRO = useHasRoleAccess(RoleEnum.CUSTOMER_OPERATOR_FOREMAN);
 */

export function useHasRoleAccess(
  target: AccessTarget,
  options?: AccessOptions
) {
  const { role } = useAuthenticatedUser();

  const result = useMemo(() => {
    let allowedRoles: RoleEnum[] = [];

    if (typeof target === "string" && target in RoleAccess) {
      allowedRoles = RoleAccess[target as RoleGroupKey];
    } else if (Array.isArray(target)) {
      allowedRoles = target;
    } else {
      allowedRoles = [target as RoleEnum];
    }

    const has = allowedRoles.includes(role);

    return options?.not ? !has : has;
  }, [role, target, options?.not]);

  return result;
}

/**
 * useHasNotRoleAccess hook
 *
 * Shortcut wrapper for `useHasRoleAccess` with `{ not: true }` option.
 *
 * const isNotAdmin = useHasNotRoleAccess('admin');
 * const cannotCreateAlert = useHasNotRoleAccess([RoleEnum.ADMIN, RoleEnum.SUPER_ADMIN]);
 */

export function useHasNotRoleAccess(target: AccessTarget) {
  return useHasRoleAccess(target, { not: true });
}
